import type { CaseDocument, DocumentType } from '@core/domain/model';
import { numeric, plural, pluralWord } from '@core/ui/RuDates';
import type { DocumentKind, DocumentListItem } from './types';

/**
 * Тексты библиотеки документов — порт `DocumentsFormatter` Android-версии.
 *
 * Экран получает готовые строки, а не модели: иначе его нельзя ни отрисовать,
 * ни проверить тестом без базы.
 */

/**
 * Названия типов — таблицей, а не цепочкой условий: перечень длинный и будет расти,
 * а читается он как список, которым и является. Полноту таблицы стережёт тест.
 *
 * Значения — «Структура» §3.2; перечень типов согласован владельцем и новых
 * в него не добавляется без согласования (CLAUDE.md, правило 7).
 */
const TYPE_LABELS: Record<DocumentType, string> = {
  Lawsuit: 'Иск',
  Motion: 'Ходатайство',
  Objection: 'Возражения',
  CourtAct: 'Судебный акт',
  Claim: 'Претензия',
  Contract: 'Договор',
  PowerOfAttorney: 'Доверенность',
  Expertise: 'Экспертиза',
  WritOfExecution: 'Исполнительный лист',
  Extract: 'Выписка',
  Correspondence: 'Переписка',
  Payment: 'Платёж',
  CaseMaterials: 'Материалы дела',
  Other: 'Без типа',
};

/** Порядок типов один на всё приложение: он же задаёт порядок разделов списка. */
export const DOCUMENT_TYPES = Object.keys(TYPE_LABELS) as DocumentType[];

export const typeLabel = (type: DocumentType): string => TYPE_LABELS[type];

export const typeOrder = (type: DocumentType): number => DOCUMENT_TYPES.indexOf(type);

export function kindOf(mimeType: string): DocumentKind {
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType.startsWith('image/')) return 'photo';

  return 'other';
}

const KIND_LABEL: Record<DocumentKind, string> = {
  pdf: 'PDF',
  photo: 'Фото',
  other: 'Файл',
};

const KILOBYTE = 1024;
const MEGABYTE = KILOBYTE * 1024;
const GIGABYTE = MEGABYTE * 1024;

/** До 10 единиц показываем десятую долю: «1,4 МБ» информативнее, чем «1 МБ». */
const TENTHS_BELOW = 10;

const round = (value: number): string =>
  value < TENTHS_BELOW ? value.toFixed(1).replace('.', ',') : String(Math.round(value));

/** Размер в тех единицах, в которых о нём думает человек: КБ, МБ, ГБ. */
export function size(bytes: number): string {
  if (bytes < KILOBYTE) return `${bytes} Б`;
  if (bytes < MEGABYTE) return `${Math.round(bytes / KILOBYTE)} КБ`;
  if (bytes < GIGABYTE) return `${round(bytes / MEGABYTE)} МБ`;

  return `${round(bytes / GIGABYTE)} ГБ`;
}

/** «5 документов» — для заголовков и диалогов, без размера. */
export const documentsCount = (count: number): string =>
  plural(count, 'документ', 'документа', 'документов');

/** «12 документов · 84 МБ» — шапка списка. */
export const summary = (count: number, totalBytes: number): string =>
  `${documentsCount(count)} · ${size(totalBytes)}`;

export function deletionMessage(titles: string[]): string {
  if (titles.length === 1) return `«${titles[0]}» удалён`;

  return `Удалено: ${titles.length} ${pluralWord(titles.length, 'документ', 'документа', 'документов')}`;
}

/** «PDF · 9 стр. · 1,2 МБ · 15.08.2026» — вид файла, объём и дата одной строкой. */
function meta(document: CaseDocument): string {
  const pages = document.pageCount === null ? null : `${document.pageCount} стр.`;

  return [KIND_LABEL[kindOf(document.mimeType)], pages, size(document.sizeBytes), numeric(new Date(document.addedAt))]
    .filter((part): part is string => Boolean(part))
    .join(' · ');
}

export function toListItem(document: CaseDocument): DocumentListItem {
  return {
    id: document.id,
    title: document.title,
    meta: meta(document),
    isFavorite: document.isFavorite,
    kind: kindOf(document.mimeType),
    source: document.source,
    type: document.type,
    // «Без типа» не показываем: отсутствие сведений — не сведение.
    typeLabel: document.type === 'Other' ? '' : typeLabel(document.type),
  };
}
